// src/routes/RequireCourseAccess.js
//
// Użycie (w App.js, razem z RequireAnyRole):
//
// <RequireAnyRole roles={['student', 'admin']}>
//   <RequireCourseAccess>
//     <HomeLayout />
//   </RequireCourseAccess>
// </RequireAnyRole>
//
// Logika:
//  - jeśli brak usera → do /login
//  - admin / superadmin → zawsze może wejść
//  - inne role (np. parent) → do /konto
//  - gdy courseAccessMode === 'open' → każdy student ma dostęp
//  - gdy courseAccessMode === 'subscription':
//       - student z entitlements.active === true → ma dostęp
//       - student bez aktywnego dostępu → do /konto/subskrypcja

import { useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AuthContext } from "../components/AuthContext";

export default function RequireCourseAccess({ children }) {
  const {
    user,
    isAuthReady,
    courseAccessMode,
    entitlements,
  } = useContext(AuthContext);
  const location = useLocation();

  if (!isAuthReady) return null;

  // brak zalogowanego – standardowo do /login
  if (!user) {
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
      />
    );
  }

  const role = user.role;

  // Administracja i korepetytor korzystają z narzędzi kursu bez subskrypcji ucznia.
  if (role === "admin" || role === "superadmin" || role === "tutor") {
    return children;
  }

  // inne role (np. parent) nie mają dostępu do treści kursu
  if (role !== "student") {
    return <Navigate to="/konto" replace />;
  }

  // tryb "open" – wszyscy uczniowie mają dostęp, niezależnie od subskrypcji
  if (courseAccessMode === "open") {
    return children;
  }

  // tryb "subscription" – wymagany aktywny dostęp
  if (courseAccessMode === "subscription" && entitlements == null) {
  return null; // albo loader
  }

  const validToTs = entitlements?.validTo ? new Date(entitlements.validTo).getTime() : null;
  const entitlementStillValid = validToTs === null || (Number.isFinite(validToTs) && validToTs > Date.now());
  const hasActiveAccess =
    entitlements?.tutoringIncluded === true ||
    (!!entitlements?.active && entitlementStillValid);

  if (!hasActiveAccess) {
    // student bez subskrypcji – kierujemy do zakładki subskrypcji
    return (
      <Navigate
        to="/konto/subskrypcja"
        state={{ from: location }}
        replace
      />
    );
  }

  // student z aktywną subskrypcją – wpuszczamy
  return children;
}
